// IMarketStatusService: abstraction for trading times and market open/close tracking
// Extracted from legacy TradingTimes logic

export type TradingTimesSymbol = {
    symbol: string;
    name?: string;
    times?: { open: string[]; close: string[] };
    events?: Array<{ dates: string; descrip: string }>;
    trading_days?: string[];
    feed_license?: string;
    delay_amount?: number;
};

export type TradingTimesMarket = {
    name: string;
    submarkets?: Array<{
        name: string;
        symbols?: TradingTimesSymbol[];
    }>;
};

export type RawTradingTimes = {
    markets: TradingTimesMarket[];
};

export type MarketStatusChanges = Record<string, boolean>; // symbol -> isOpen

export type FetchTradingTimesFn = (date?: string) => Promise<RawTradingTimes>;
export type GetServerTimeFn = () => number; // epoch seconds

export interface IMarketStatusService {
    init(): Promise<void>;
    isInitialized(): boolean;
    isMarketOpen(symbol: string): boolean | undefined;
    isFeedUnavailable(symbol: string): boolean | undefined;
    getDelayedMinutes(symbol: string): number | undefined;
    onMarketStatusChange(cb: (changes: MarketStatusChanges) => void): () => void;
    refresh(date?: string): Promise<void>;
    destroy(): void;
}

export interface MarketStatusServiceOptions {
    fetchTradingTimes: FetchTradingTimesFn;
    getServerTime: GetServerTimeFn;
    shouldFetch?: boolean; // default true
    initialData?: RawTradingTimes;
}

type TradingTimesItem = {
    feed_license?: string;
    delay_amount: number;
    times?: Array<{ open: Date; close: Date }>;
    isOpenAllDay?: boolean;
    isClosedAllDay?: boolean;
    isClosedToday?: boolean;
    holidays: string[];
    closes_early: Array<{ date: string; open: Date; close: Date }>;
    opens_late: Array<{ date: string; open: Date; close: Date }>;
    isOpened?: boolean;
};

const DaysOfWeek = ['Sundays', 'Mondays', 'Tuesdays', 'Wednesdays', 'Thursdays', 'Fridays', 'Saturdays'];
const FEED_UNAVAILABLE = 'chartonly';

export class MarketStatusService implements IMarketStatusService {
    private _opts: MarketStatusServiceOptions;
    private _tradingTimesMap: Record<string, TradingTimesItem> = {};
    private _listeners: Set<(changes: MarketStatusChanges) => void> = new Set();
    private _updateTimer?: ReturnType<typeof setTimeout>;
    private _initialized = false;
    private _lastUpdateDate?: string;

    constructor(opts: MarketStatusServiceOptions) {
        this._opts = { shouldFetch: true, ...opts };
    }

    async init(): Promise<void> {
        if (this._initialized) return;
        this._initialized = true;
        const now = this._getLocalDate();
        this._lastUpdateDate = now.toISOString().substring(0, 10);

        if (this._opts.shouldFetch !== false || this._opts.initialData) {
            await this._updateTradingTimes(this._lastUpdateDate);
            this._startPeriodicUpdate();
        }
    }

    isInitialized(): boolean {
        return this._initialized;
    }

    isMarketOpen(symbol: string): boolean | undefined {
        if (!this._tradingTimesMap[symbol]) return undefined;
        return this._tradingTimesMap[symbol].isOpened;
    }

    isFeedUnavailable(symbol: string): boolean | undefined {
        if (!this._tradingTimesMap[symbol]) return undefined;
        return this._tradingTimesMap[symbol].feed_license === FEED_UNAVAILABLE;
    }

    getDelayedMinutes(symbol: string): number | undefined {
        return this._tradingTimesMap[symbol]?.delay_amount;
    }

    onMarketStatusChange(cb: (changes: MarketStatusChanges) => void): () => void {
        this._listeners.add(cb);
        return () => this._listeners.delete(cb);
    }

    async refresh(date?: string): Promise<void> {
        const targetDate = date || this._getLocalDate().toISOString().substring(0, 10);
        await this._updateTradingTimes(targetDate);
    }

    destroy(): void {
        if (this._updateTimer) {
            clearTimeout(this._updateTimer);
            this._updateTimer = undefined;
        }
        this._listeners.clear();
    }

    private _getLocalDate(): Date {
        const epoch = this._opts.getServerTime();
        return new Date(epoch * 1000);
    }

    private async _updateTradingTimes(date: string): Promise<void> {
        let raw: RawTradingTimes | undefined;
        if (this._opts.initialData && Object.keys(this._tradingTimesMap).length === 0) {
            raw = this._opts.initialData;
        } else if (this._opts.shouldFetch !== false) {
            raw = await this._opts.fetchTradingTimes(date);
        } else {
            return;
        }
        if (raw) {
            this._processTradingTimes(raw);
        }
    }

    private _processTradingTimes(raw: RawTradingTimes): void {
        this._tradingTimesMap = {};
        const now = this._getLocalDate();
        const dateStr = now.toISOString().substring(0, 11); // YYYY-MM-DDT
        const getUTCDate = (hour: string) => new Date(`${dateStr}${hour}Z`);

        for (const market of raw.markets) {
            for (const submarket of market.submarkets || []) {
                for (const symbolObj of submarket.symbols || []) {
                    const { symbol, times, events = [], trading_days = [], feed_license, delay_amount = 0 } = symbolObj;
                    if (!times) continue;

                    const { open, close } = times;
                    let isClosedToday = trading_days.length > 0 && trading_days.every(day => day !== DaysOfWeek[now.getDay()].slice(0, 3));
                    const holidays: string[] = [];
                    const closes_early: Array<{ date: string; open: Date; close: Date }> = [];
                    const opens_late: Array<{ date: string; open: Date; close: Date }> = [];

                    events
                        .flatMap(event =>
                            event.dates.includes(',')
                                ? event.dates.split(',').map(d => ({ date: d.trim(), description: event.descrip }))
                                : [{ date: event.dates, description: event.descrip }]
                        )
                        .forEach(evt => {
                            const lower = evt.description.toLowerCase();
                            const opensLateMatch = /^opens late \(at (\d{1,2}:\d{1,2})\)$/.exec(lower);
                            const closesEarlyMatch = /^closes early \(at (\d{1,2}:\d{1,2})\)$/.exec(lower);

                            if (opensLateMatch) {
                                const eventHour = `${opensLateMatch[1]}:00`;
                                const eventDate =
                                    evt.date === 'today' || evt.date === DaysOfWeek[now.getDay()]
                                        ? dateStr.substring(0, 10)
                                        : evt.date;
                                opens_late.push({
                                    date: eventDate,
                                    open: new Date(`${eventDate}T${eventHour}Z`),
                                    close: new Date(`${eventDate}T${close[0]}Z`),
                                });
                            } else if (closesEarlyMatch) {
                                const eventHour = `${closesEarlyMatch[1]}:00`;
                                const eventDate =
                                    evt.date === 'today' || evt.date === DaysOfWeek[now.getDay()]
                                        ? dateStr.substring(0, 10)
                                        : evt.date;
                                closes_early.push({
                                    date: eventDate,
                                    open: new Date(`${eventDate}T${open[0]}Z`),
                                    close: new Date(`${eventDate}T${eventHour}Z`),
                                });
                            } else if (/^\d{4}-\d{2}-\d{2}$/.test(evt.date)) {
                                holidays.push(evt.date);
                            }
                        });

                    const isOpenAllDay = open.length === 1 && open[0] === '00:00:00' && close[0] === '23:59:59';
                    const isClosedAllDay = open.length === 1 && open[0] === '--' && close[0] === '--';

                    let _times: Array<{ open: Date; close: Date }> | undefined;
                    if (!isOpenAllDay && !isClosedAllDay) {
                        _times = open.map((o, idx) => ({ open: getUTCDate(o), close: getUTCDate(close[idx]) }));
                    }

                    this._tradingTimesMap[symbol] = {
                        feed_license,
                        delay_amount,
                        times: _times,
                        isOpenAllDay,
                        isClosedAllDay,
                        isClosedToday,
                        holidays,
                        closes_early,
                        opens_late,
                    };
                }
            }
        }
        const changes = this._updateMarketOpenClosed();
        if (Object.keys(changes).length > 0) {
            this._emitChanges(changes);
        }
    }

    private _updateMarketOpenClosed(): MarketStatusChanges {
        const changes: MarketStatusChanges = {};
        for (const sym in this._tradingTimesMap) {
            const isOpened = this._calcIsMarketOpen(sym);
            if (this._tradingTimesMap[sym].isOpened !== isOpened) {
                this._tradingTimesMap[sym].isOpened = isOpened;
                changes[sym] = isOpened;
            }
        }
        return changes;
    }

    private _calcIsMarketOpen(symbol: string): boolean {
        const now = this._getLocalDate();
        const dateStr = now.toISOString().substring(0, 10);
        const item = this._tradingTimesMap[symbol];
        if (!item) return false;

        const { isOpenAllDay, isClosedAllDay, feed_license, isClosedToday, holidays, opens_late, closes_early, times } = item;

        if (isClosedAllDay || feed_license === FEED_UNAVAILABLE || isClosedToday || holidays.includes(dateStr)) {
            return false;
        }

        const opensLateToday = opens_late.find(e => e.date === dateStr);
        if (opensLateToday) {
            return now >= opensLateToday.open && now < opensLateToday.close;
        }

        const closesEarlyToday = closes_early.find(e => e.date === dateStr);
        if (closesEarlyToday) {
            return now >= closesEarlyToday.open && now < closesEarlyToday.close;
        }

        if (isOpenAllDay) return true;

        if (times) {
            for (const session of times) {
                if (now >= session.open && now < session.close) return true;
            }
        }
        return false;
    }

    private _startPeriodicUpdate(): void {
        const periodicUpdate = async () => {
            const changes = this._updateMarketOpenClosed();
            if (Object.keys(changes).length > 0) {
                this._emitChanges(changes);
            }

            let nextUpdate = this._nextUpdateDate();
            if (!nextUpdate) {
                // Rollover to next day
                const now = this._getLocalDate();
                const nextDay = new Date(`${this._lastUpdateDate}T00:00:00Z`);
                nextDay.setDate(nextDay.getDate() + 1);
                this._lastUpdateDate = (now > nextDay ? now : nextDay).toISOString().substring(0, 10);

                const isOpenMap: Record<string, boolean> = {};
                for (const key in this._tradingTimesMap) {
                    isOpenMap[key] = !!this._tradingTimesMap[key].isOpened;
                }
                await this._updateTradingTimes(this._lastUpdateDate);
                for (const key in this._tradingTimesMap) {
                    this._tradingTimesMap[key].isOpened = isOpenMap[key];
                }
                nextUpdate = nextDay;
            }

            const waitPeriod = (nextUpdate as any) - (this._getLocalDate() as any);
            this._updateTimer = setTimeout(periodicUpdate, waitPeriod);
        };
        periodicUpdate();
    }

    private _nextUpdateDate(): Date | undefined {
        const now = this._getLocalDate();
        let nextDate: Date | undefined;
        for (const key in this._tradingTimesMap) {
            const { times, feed_license, isOpenAllDay, isClosedAllDay } = this._tradingTimesMap[key];
            if (isOpenAllDay || isClosedAllDay || feed_license === FEED_UNAVAILABLE) continue;
            if (times) {
                for (const session of times) {
                    if (session.open > now && (!nextDate || session.open < nextDate)) nextDate = session.open;
                    if (session.close > now && (!nextDate || session.close < nextDate)) nextDate = session.close;
                }
            }
        }
        return nextDate;
    }

    private _emitChanges(changes: MarketStatusChanges): void {
        for (const listener of this._listeners) {
            listener(changes);
        }
    }
}

export default MarketStatusService;
